import { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";

import { ArrowLeft, CheckCircle, Maximize2 } from "lucide-react";
import "../styles/scansheet.css";
import { logoutProcessor } from "../components/helpers/logoutProcessor";
import { playGreenHotSound, playGreenColdSound, playError, playSuccess } from "../components/helpers/audioPlayer";
import { toast } from "react-toastify";
import { useTranslation } from "../i18n";
import HiddenScanInput from "../components/HiddenScanInput";
import FitWord from "../components/FitWord";
import ScanStatusIcons from "../components/ScanStatusIcons";
import ScanFlashOverlay from "../components/ScanFlashOverlay";
import {
    preventScanPageButtonFocus,
    blurClickedScanPageButton,
} from "../components/helpers/scanFocusGuard";
import {
    useProgressTimer,
    useStreak,
    useBestTime,
    useSessionScore,
    ProgressBar,
    TimeBonusPopup,
    ScoreDisplay,
} from "../components/gamification";
import { silentPrintBarcode, isPrintAgentAvailable, resolvePrintAgentDownloadUrl } from "../components/helpers/printAgent";
import { wasRecentlyScanned, markScanned } from "../components/helpers/recentScans";
import { submitNetworkPrintJob, getSavedNetworkPrinter, isSavedNetworkPrinterOnline, detectPrintMode } from "../components/helpers/networkPrint";
import PrinterSelectModal from "../components/PrinterSelectModal";
import greenHotImg from "../images/green-hot.png";
import greenColdImg from "../images/green-cold.png";
import { apiFetch } from "../api/apiFetch";
import BfScanSheet from "../components/bf-scan/BfScanSheet";

// Per-session location lock — operators must scan a location barcode before
// they can scan items. The barcode is cached in sessionStorage so a refresh
// or a brief navigate-away doesn't force a re-scan, but the cache expires
// after this much idle time so an abandoned tab doesn't keep impersonating
// a Position. Logout clears the cache via logoutProcessor.
const LOCATION_CACHE_KEY = "scansheet_active_location";
const LOCATION_IDLE_MS = 30 * 60 * 1000; // 30 minutes

type colorData = { [key: string]: string };

type ScanStage = "location" | "items" | "bf";
// `productData` = the desk's "Box contents" flag: when set, every /scan/check
// asks for getProductData and the ASINs inside the box are listed under the
// PkgID.
type ActiveLocation = { barcode: string; position: string; warehouse: string; bargainfox: boolean; productData: boolean };
type LocationCacheEntry = { barcode: string; bargainfox?: boolean; productData?: boolean; lastAccessAt: number };
// One row per ASIN from /scan/check `product_data` (units already summed
// per ASIN by the BE). `null` = contents not requested / not returned for
// this scan; `[]` = requested but nothing on file.
type BoxContentRow = { Asin: string; ItemDesc: string; Units: number; UnitCost: number };

// Box info layout: the table is clipped to whatever fits above the bottom
// scan bar (16px inset + up to two 44px button rows + gap) plus breathing
// room, so the "Last: ..." readout never gets covered.
const BOX_INFO_BOTTOM_RESERVE_PX = 150;
// Long Amazon titles: on handhelds they are capped at 45 chars and may wrap;
// on desktop (>= the 640px breakpoint scansheet.css uses) the full title is
// rendered and CSS clips it to one line with an ellipsis, so it uses however
// much width the screen gives the title column.
const BOX_INFO_TITLE_MAX_MOBILE = 45;
const BOX_INFO_DESKTOP_MIN_WIDTH = 640;

const capTitle = (raw: string, max: number | null) =>
    max !== null && raw.length > max ? raw.slice(0, max - 1).trimEnd() + "\u2026" : raw;

const readLocationCache = (): LocationCacheEntry | null => {
    try {
        const raw = window.sessionStorage.getItem(LOCATION_CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<LocationCacheEntry>;
        if (typeof parsed?.barcode !== "string" || typeof parsed?.lastAccessAt !== "number") return null;
        return { barcode: parsed.barcode, bargainfox: parsed.bargainfox === true, productData: parsed.productData === true, lastAccessAt: parsed.lastAccessAt };
    } catch {
        return null;
    }
};

const writeLocationCache = (barcode: string, bargainfox = false, productData = false) => {
    try {
        const entry: LocationCacheEntry = { barcode, bargainfox, productData, lastAccessAt: Date.now() };
        window.sessionStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify(entry));
    } catch {
        // sessionStorage quota / privacy modes — failing to cache only forces
        // the operator to re-scan after a refresh, which is acceptable.
    }
};

const clearLocationCache = () => {
    try { window.sessionStorage.removeItem(LOCATION_CACHE_KEY); } catch { /* ignore */ }
};

const ScanSheet = () => {
    usePageTitle("scan_sheet");
    const navigator = useNavigate();
    const { t } = useTranslation();

    const userData = window.localStorage.getItem("user");
    const userObject =
        userData !== null ? JSON.parse(userData) : { uid: 0, name: "", permissions: "", warehouse: "Unknown" };

    const [loading, set_loading] = useState(false);
    const [scanValue, set_scanValue] = useState("");
    const [scannedData, set_scannedData] = useState({
        PkgID: "", PO: "", RemovalReason: "", "PO Name": "", Supplier: "",
    });
    const [boxColour, set_boxColour] = useState("");
    const [boxContents, set_boxContents] = useState<BoxContentRow[] | null>(null);
    const [scanStatus, set_scanStatus] = useState<"idle" | "success" | "error">("idle");
    const [lastPrintValue, setLastPrintValue] = useState<string | null>(null);
    const [lastScannedValue, setLastScannedValue] = useState<string | null>(null);
    const statusTimerRef = useRef<NodeJS.Timeout | null>(null);
    const resetTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Two-stage flow: every session starts at "location" until the operator
    // scans a known location barcode (NPGI / NPPA / etc.). Once resolved,
    // `activeLocation` drives the Warehouse + Position fields on every
    // /scan/log entry instead of the operator's defaults.
    const [stage, setStage] = useState<ScanStage>("location");
    const [activeLocation, setActiveLocation] = useState<ActiveLocation | null>(null);
    const [bfResumeFromCache, setBfResumeFromCache] = useState(false);
    const activeLocationRef = useRef<ActiveLocation | null>(null);
    useEffect(() => { activeLocationRef.current = activeLocation; }, [activeLocation]);

    // Gamification
    // Scan sheet is a calibration flow of the ~2,000 pts/engaged-hr anchor
    // (migrations 169/171) — 6s IS the anchor; don't tune it in isolation.
    const DEFAULT_DURATION_MS = 6000;
    const STORAGE_DEVICE_BONUS_MS = 5000;
    const BANNED_BONUS_MS = 10000;
    const RED_BONUS_MS = 15000;
    const YELLOW_BONUS_MS = 15000;
    const RELABEL_BONUS_MS = 3000;
    const HEAVY_BONUS_MS = 2000;
    const HEAVIER_BONUS_MS = 2000;
    const KEYBOARD_BONUS_MS = 20000;
    const ERROR_PAUSE_MS = 2000;

    const timer = useProgressTimer();
    const streakApi = useStreak();
    const bestTime = useBestTime("scansheet-best-time");
    const score = useSessionScore();

    const [timeBonus, set_timeBonus] = useState<number | null>(null);
    const [timeBonusColor, set_timeBonusColor] = useState<"green" | "amber">("green");
    const [isBestTime, set_isBestTime] = useState(false);
    const timeBonusRef = useRef<NodeJS.Timeout | null>(null);
    const timeBonusKeyRef = useRef(0);
    const nextTimerBonusRef = useRef(0);
    const keyboardOpenRef = useRef(false);
    const suspectScanRef = useRef(false);

    // Per-PO timer targets fetched from warehouse settings. Missing/empty
    // entries fall back to DEFAULT_DURATION_MS.
    const poTimersRef = useRef<Record<string, number>>({});

    // On mount, restore a cached location if it's recent enough. We re-validate
    // against the BE so a soft-deleted barcode bounces the operator back to
    // the location-scan stage and an admin edit (e.g. NPGI moved from Newport
    // to Poland) takes effect on the very next page load instead of being
    // trapped behind the 30-min idle window. We only cache the *barcode*,
    // never the resolved position/warehouse.
    useEffect(() => {
        const cached = readLocationCache();
        if (!cached) return;
        if (Date.now() - cached.lastAccessAt > LOCATION_IDLE_MS) {
            clearLocationCache();
            return;
        }
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(`/scansheet-admin/location/check`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ scannedItem: cached.barcode, barcode: cached.barcode }),
                });
                if (!res.ok) return;
                const data = await res.json();
                if (!alive) return;
                if (data?.found) {
                    setActiveLocation({
                        barcode: data.barcode,
                        position: data.position,
                        warehouse: data.warehouse,
                        bargainfox: data.bargainfox === true,
                        productData: data.productData === true,
                    });
                    writeLocationCache(data.barcode, data.bargainfox === true, data.productData === true);
                    setBfResumeFromCache(data.bargainfox === true);
                    setStage(data.bargainfox === true ? "bf" : "items");
                } else {
                    clearLocationCache();
                }
            } catch {
                // Network blip — leave the operator on the location-scan stage
                // rather than silently using stale cached state.
            }
        })();
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        const warehouse = userObject.warehouse;
        if (!warehouse) return;
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(
                    `/settings/${encodeURIComponent(warehouse)}`,
                    {},
                );
                if (!res.ok) return;
                const data = await res.json();
                if (!alive) return;
                if (data.scan_sheet_po_timers && typeof data.scan_sheet_po_timers === "object") {
                    poTimersRef.current = data.scan_sheet_po_timers;
                }
            } catch {
                // leave defaults
            }
        })();
        return () => { alive = false; };
    }, [userObject.warehouse]);

    const computeNextDurationMs = (po?: string | null) => {
        const custom = po ? poTimersRef.current[po] : undefined;
        const base = typeof custom === "number" && custom > 0 ? custom : DEFAULT_DURATION_MS;
        return base + nextTimerBonusRef.current + (keyboardOpenRef.current ? KEYBOARD_BONUS_MS : 0);
    };

    const resetDisplay = () => {
        set_scannedData({ PkgID: "", PO: "", RemovalReason: "", "PO Name": "", Supplier: "" });
        set_boxColour("");
        set_boxContents(null);
    };

    const triggerResetDisplayCountdown = () => {
        if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
        // Box info desks: no auto-clear at all - the result stays on screen
        // until the next scan replaces it.
        if (activeLocationRef.current?.productData === true) return;
        resetTimerRef.current = setTimeout(() => {
            resetDisplay();
        }, 10000);
    };

    // Print state — supports local (print agent) and network (backend relay) modes
    const [autoPrint, setAutoPrint] = useState(false);
    const [printChecking, setPrintChecking] = useState(() => localStorage.getItem("scansheet-auto-print") === "true");
    const [printMode, _setPrintMode] = useState<"local" | "network" | null>(null);
    const setPrintMode = (mode: "local" | "network" | null) => {
        _setPrintMode(mode);
        if (mode) localStorage.setItem("scansheet-print-mode", mode);
        else localStorage.removeItem("scansheet-print-mode");
    };
    const [printerOnline, setPrinterOnline] = useState(true);
    const [showPrinterSelect, setShowPrinterSelect] = useState(false);
    const autoPrintRef = useRef(false);
    const printModeRef = useRef<"local" | "network" | null>(null);
    const printerOnlineRef = useRef(true);
    useEffect(() => { autoPrintRef.current = autoPrint; }, [autoPrint]);
    useEffect(() => { printModeRef.current = printMode; }, [printMode]);
    useEffect(() => { printerOnlineRef.current = printerOnline; }, [printerOnline]);

    // On mount, restore saved printer preference and verify it's online.
    // If the printer is offline we auto-disconnect (autoPrint=false) but keep
    // the localStorage preference so we'll reconnect when it comes back.
    useEffect(() => {
        const saved = localStorage.getItem("scansheet-auto-print") === "true";
        const savedMode = localStorage.getItem("scansheet-print-mode");
        if (!saved) { setPrintChecking(false); return; }

        const connectIfOnline = (online: boolean, mode: "local" | "network") => {
            if (online) {
                setAutoPrint(true);
                setPrintMode(mode);
                setPrinterOnline(true);
            } else {
                // Auto-disconnect — saved printer is offline. Clear React state
                // so the modal shows disconnected; localStorage preference stays.
                setAutoPrint(false);
                _setPrintMode(null);
                setPrinterOnline(false);
            }
            setPrintChecking(false);
        };

        if (savedMode === "network" && getSavedNetworkPrinter()) {
            isSavedNetworkPrinterOnline().then(online => connectIfOnline(online, "network"));
        } else if (savedMode === "local") {
            isPrintAgentAvailable().then(online => connectIfOnline(online, "local"));
        } else {
            // Legacy: no saved mode — try local, fall back to network
            isPrintAgentAvailable().then(available => {
                if (available) {
                    connectIfOnline(true, "local");
                } else if (getSavedNetworkPrinter()) {
                    isSavedNetworkPrinterOnline().then(online => connectIfOnline(online, "network"));
                } else {
                    setAutoPrint(false);
                    setPrinterOnline(false);
                    setPrintChecking(false);
                }
            });
        }
    }, []);

    // Prompt to install the print agent ONLY when this device has no print
    // route at all — no local agent AND no usable network printer. Machines
    // printing via a network printer are fully set up and must not be
    // nagged. No outdated-version prompt: agents ≥ 1.4.0 self-update from
    // the BE, and operators can't run the installer anyway (needs admin) —
    // the printer modal + admin Printers page carry the passive version
    // hints for IT. Desktop only — mobile can't run the agent.
    const isMobile = /Mobi|Android|iPhone|iPad/i.test(window.navigator.userAgent);
    useEffect(() => {
        if (isMobile) return;
        detectPrintMode().then(async mode => {
            if (mode !== null) return;
            const downloadUrl = await resolvePrintAgentDownloadUrl();
            toast.dismiss();
            toast.warn(
                <div>
                    {t("print_agent_not_installed") || "Print Agent not installed"}
                    <br />
                    <a
                        href={downloadUrl}
                        style={{ color: "white", textDecoration: "underline", fontWeight: 600 }}
                    >
                        {t("download_print_agent") || "Download Print Agent"}
                    </a>
                </div>,
                { autoClose: 15000 }
            );
        });
    }, [t]);

    // Periodically check if the selected printer is still online (every 30s).
    // If it goes offline, auto-disconnect so the icon turns red and the modal
    // doesn't show a stale "connected" state.
    useEffect(() => {
        if (!autoPrint || !printMode) return;

        const checkOnline = async () => {
            const online = printMode === "local"
                ? await isPrintAgentAvailable()
                : await isSavedNetworkPrinterOnline();
            setPrinterOnline(online);
            if (!online) {
                // Auto-disconnect — clear React state, keep localStorage
                setAutoPrint(false);
                _setPrintMode(null);
            }
        };

        const interval = setInterval(checkOnline, 30_000);
        return () => clearInterval(interval);
    }, [autoPrint, printMode]);

    const openPrinterModal = useCallback(() => {
        setShowPrinterSelect(true);
    }, []);

    // Print barcode label — local or network
    const [printFlash, setPrintFlash] = useState(false);
    const printBarcode = useCallback(async (barcodeValue: string) => {
        setPrintFlash(true);
        setTimeout(() => setPrintFlash(false), 500);

        // Try network if mode is network OR if no local agent and a network printer is saved
        const useNetwork = printModeRef.current === "network" || (!printModeRef.current && getSavedNetworkPrinter());

        if (useNetwork) {
            const saved = getSavedNetworkPrinter();
            if (saved) {
                const { buildBarcodeLabel } = await import("../components/helpers/printAgent");
                // Pass both the HTML fallback AND the raw barcode value so the
                // agent can use ZPL fast-path for Zebra printers.
                const result = await submitNetworkPrintJob(
                    saved.agentId,
                    saved.printerName,
                    buildBarcodeLabel(barcodeValue),
                    1,
                    barcodeValue,
                );
                if (!result.success) {
                    toast.dismiss();
                    toast.error(result.error || t("print_job_failed"));
                }
                return;
            }
        }

        // Local print
        const result = await silentPrintBarcode(barcodeValue);
        if (!result.success) {
            toast.dismiss();
            toast.error(result.error || t("print_failed"));
        }
    }, [t]);

    const colours: colorData = {
        "RED": "#FF1617", "YELLOW": "#FFF737", "GREEN": "#7ED957",
        "GREEN-HOT": "#16a34a", "GREEN-COLD": "#86efac", "GREEN HOT": "#16a34a", "GREEN COLD": "#86efac",
        "BLUE": "#38B6FF", "PINK": "#FF66C4", "ORANGE": "#FF914D", "GREY": "#606060",
        "Relabel": "#7e187a", "BANNED": "#000000", "STORAGE-DEVICE": "#FFFFFF", "PRICING-DISPUTE": "#FFFFFF", "": "white",
        "ERROR": "#000000", "SPECIAL": "#FFD700"
    };

    const isDarkBg = ["RED", "GREY", "BANNED", "BLUE", "PINK", "Relabel", "GREEN", "GREEN-HOT", "GREEN HOT", "ERROR"].includes(boxColour);
    const isLightGreen = boxColour === "GREEN-COLD" || boxColour === "GREEN COLD";
    const isStorageDevice = boxColour === "STORAGE-DEVICE";
    const isError = boxColour === "ERROR";

    // ---- Box info desks: PO + SKU header, RRP summary and a fitted table ----
    const boxInfoMode = activeLocation?.productData === true;
    const showBoxInfo = boxInfoMode && !!scannedData.PkgID && boxContents !== null && !isError;
    // Highest-value lines first so a clipped table still shows what matters.
    const boxInfoRows = useMemo(
        () => (boxContents ?? []).slice().sort((a, b) => (b.UnitCost - a.UnitCost) || (b.Units - a.Units)),
        [boxContents],
    );
    const boxInfoTotals = useMemo(
        () => boxInfoRows.reduce((acc, r) => ({ units: acc.units + r.Units, rrp: acc.rrp + r.Units * r.UnitCost }), { units: 0, rrp: 0 }),
        [boxInfoRows],
    );
    // RRP currency follows the desk's warehouse: Poland stock is euro-priced,
    // everything else sterling. Products carry no currency of their own.
    const boxInfoCurrency = activeLocation?.warehouse === "Poland" ? "\u20ac" : "\u00a3";
    const fmtMoney = (n: number) =>
        `${boxInfoCurrency}${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    // `null` = measuring pass (every row rendered); a number = rows that fit.
    const [boxInfoFit, set_boxInfoFit] = useState<number | null>(null);
    const boxInfoBodyRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        const onResize = () => set_boxInfoFit(null);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);
    useLayoutEffect(() => {
        if (!showBoxInfo) { set_boxInfoFit(null); return; }
        if (boxInfoFit !== null) return;
        const body = boxInfoBodyRef.current;
        if (!body) return;
        const rows = Array.from(body.querySelectorAll<HTMLElement>("[data-boxinfo-row]"));
        const more = body.querySelector<HTMLElement>("[data-boxinfo-more]");
        const limit = window.innerHeight - BOX_INFO_BOTTOM_RESERVE_PX;
        const countFitting = (bottomLimit: number) => {
            let n = 0;
            for (const r of rows) {
                if (r.getBoundingClientRect().bottom <= bottomLimit) n++;
                else break;
            }
            return n;
        };
        const all = countFitting(limit);
        if (all >= rows.length) { set_boxInfoFit(rows.length); return; }
        // Truncated: keep space for the "+N more" line under the last row.
        const moreH = more ? more.getBoundingClientRect().height : 0;
        set_boxInfoFit(Math.max(0, countFitting(limit - moreH)));
    }, [showBoxInfo, boxInfoRows, boxInfoFit]);
    const boxInfoVisible = boxInfoFit === null ? boxInfoRows : boxInfoRows.slice(0, boxInfoFit);
    // Re-evaluated on every render; the resize listener above re-renders.
    // null = no character cap (desktop, CSS single-line ellipsis instead).
    const boxInfoTitleMax = window.innerWidth >= BOX_INFO_DESKTOP_MIN_WIDTH ? null : BOX_INFO_TITLE_MAX_MOBILE;
    const boxInfoHidden = boxInfoRows.length - boxInfoVisible.length;

    // Show status icon in centre, auto-clear after 1000ms
    const flashStatus = (status: "success" | "error") => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        set_scanStatus(status);
        statusTimerRef.current = setTimeout(() => set_scanStatus("idle"), 800);
    };

    // Cleanup timers on unmount
    useEffect(() => () => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    }, []);

    const createLog = async (pkgId: string, colour: string, weight: number) => {
        // Position + Warehouse come from the location barcode the operator
        // scanned at the start of the session. We refuse to log without one
        // — gating on stage="items" before doSubmit reaches here means this
        // guard is belt-and-braces.
        const loc = activeLocationRef.current;
        if (!loc) return;
        await apiFetch(`/scan/log`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                PkgID: pkgId, Colour: colour, Weight: weight,
                UID: userObject.uid, User: userObject.name,
                Warehouse: loc.warehouse, Position: loc.position,
            })
        }).then(async (res): Promise<any> => {
            if (res.ok) return;
            else if (res.status === 401) { logoutProcessor(); navigator("/login"); }
            // Log failures are silent — don't reset the scan result colour
        });
    };

    // Stage = "location": resolve the scanned barcode against the admin-
    // managed location list. On success, lock the session to that location
    // and switch to item-scanning. On miss, the operator gets the standard
    // error flash + sound and the input clears for another attempt.
    const submitLocationScan = async (val: string) => {
        set_loading(true);
        try {
            const res = await apiFetch(`/scansheet-admin/location/check`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ scannedItem: val, barcode: val }),
            });
            if (!res.ok) {
                playError();
                flashStatus("error");
                toast.dismiss(); toast.error(t("location_check_failed"));
                return;
            }
            const data = await res.json();
            if (data?.found) {
                const next: ActiveLocation = {
                    barcode: data.barcode,
                    position: data.position,
                    warehouse: data.warehouse,
                    bargainfox: data.bargainfox === true,
                    productData: data.productData === true,
                };
                setActiveLocation(next);
                writeLocationCache(next.barcode, next.bargainfox, next.productData);
                setBfResumeFromCache(false);
                setStage(next.bargainfox ? "bf" : "items");
                // No success tick here — the location scan is a pre-step and the
                // checkmark just gets in the way before item scanning begins.
                playSuccess();
            } else {
                playError();
                flashStatus("error");
                toast.dismiss(); toast.error(t("unknown_location_barcode"));
            }
        } catch {
            playError();
            flashStatus("error");
            toast.dismiss(); toast.error(t("location_check_failed"));
        } finally {
            set_loading(false);
            set_scanValue("");
        }
    };

    const doSubmit = async (scannedValue?: string) => {
        const val = scannedValue ?? scanValue;
        if (val === "") {
            toast.dismiss(); toast.error(t("nothing_scanned"));
            return;
        }
        // Pre-stage: every value goes through the location lookup until one
        // resolves. Item-scan path below is unreachable while stage="location".
        if (stage === "location") {
            await submitLocationScan(val);
            return;
        }
        // Bump the cache timestamp so an actively-scanning operator never
        // times out mid-shift.
        if (activeLocationRef.current) {
            writeLocationCache(activeLocationRef.current.barcode, activeLocationRef.current.bargainfox, activeLocationRef.current.productData);
        }
        if (val !== "") {
            if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
            set_loading(true);
            setLastScannedValue(val);

            // Desks flagged "Box contents" ask the BE to attach the ASINs
            // inside the box (grouped per ASIN, units summed).
            const wantBoxContents = activeLocationRef.current?.productData === true;
            await apiFetch(`/scan/check`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ scannedItem: val, getProductData: wantBoxContents })
            }).then(async (res): Promise<any> => {
                let fullResponse = await res.json();
                let response = fullResponse.response;

                if (res.ok) {
                    set_scannedData({
                        PkgID: response.PkgID, PO: response.PO,
                        RemovalReason: response.RemovalReason, "PO Name": response["PO Name"],
                        Supplier: response.Supplier ?? "",
                    });
                    set_boxColour(response.PO);
                    // The BE omits product_data for relabel / reprint / LPN
                    // results even when asked, so null there (nothing shown)
                    // rather than an empty "no contents" line.
                    set_boxContents(
                        wantBoxContents && Array.isArray(fullResponse.product_data)
                            ? fullResponse.product_data.map((row: any) => ({
                                Asin: String(row?.Asin ?? ""),
                                ItemDesc: String(row?.ItemDesc ?? ""),
                                Units: Number(row?.Units) || 0,
                                UnitCost: Number(row?.UnitCost) || 0,
                            }))
                            : null,
                    );
                    flashStatus("success");
                    playSuccess();

                    // Gamification: close the previous scan's window, award bonus,
                    // and start the next window with any stacked duration bonuses.
                    if (response.PkgID) {
                        const isDuplicate = wasRecentlyScanned(val);
                        const isSuspect = suspectScanRef.current;
                        suspectScanRef.current = false;
                        if (timer.state.active) {
                            const { elapsedMs, completed, durationMs } = timer.stop();
                            if (isSuspect || isDuplicate) {
                                // Suspect scan (scanner/paste while keyboard was open),
                                // or the same barcode scanned twice in a row: no bonus,
                                // no streak change, no popup.
                                set_timeBonus(null);
                                set_isBestTime(false);
                            } else if (!completed && durationMs > 0) {
                                const secondsLeft = Math.max(0, Math.ceil((durationMs - elapsedMs) / 1000));
                                set_timeBonusColor(elapsedMs / durationMs < 0.5 ? "green" : "amber");
                                const { isNewBest, prevExisted } = bestTime.check(elapsedMs);
                                set_isBestTime(isNewBest && prevExisted);
                                streakApi.increment();
                                score.addSeconds(secondsLeft, "scan_sheet");

                                timeBonusKeyRef.current += 1;
                                set_timeBonus(secondsLeft);
                                if (timeBonusRef.current) clearTimeout(timeBonusRef.current);
                                timeBonusRef.current = setTimeout(() => {
                                    set_timeBonus(null);
                                    set_isBestTime(false);
                                }, 1500);
                            } else {
                                set_timeBonus(null);
                                set_isBestTime(false);
                                streakApi.reset();
                            }
                        }
                        markScanned(val);

                        // Stackable duration bonuses awarded to the next timer.
                        // The API's classifier lives in `PO` (e.g. "GREEN-COLD",
                        // "BANNED", "RED", "STORAGE-DEVICE"); some older scans
                        // also expose it as `Colour`, so check both.
                        const tag = response.PO ?? response.Colour;
                        let nextBonus = 0;
                        if (tag === "STORAGE-DEVICE") nextBonus += STORAGE_DEVICE_BONUS_MS;
                        if (tag === "BANNED") nextBonus += BANNED_BONUS_MS;
                        if (tag === "RED") nextBonus += RED_BONUS_MS;
                        if (tag === "YELLOW") nextBonus += YELLOW_BONUS_MS;
                        if (tag === "Relabel" || response["PO Name"] === "Relabel") nextBonus += RELABEL_BONUS_MS;
                        const weight = Number(response.Weight);
                        if (Number.isFinite(weight) && weight > 5000) nextBonus += HEAVY_BONUS_MS;
                        if (Number.isFinite(weight) && weight > 10000) nextBonus += HEAVIER_BONUS_MS;
                        nextTimerBonusRef.current = nextBonus;
                        timer.start(computeNextDurationMs(tag));
                    }

                    if (response.PO === "GREEN-HOT") playGreenHotSound();
                    else if (response.PO === "GREEN-COLD") playGreenColdSound();

                    // Print barcode label if enabled and:
                    // - Item is Relabel → print PkgID
                    // - TNT consignment number → print PkgID
                    // Save last scan for reprint
                    if (response.PkgID) setLastPrintValue(response.PkgID);

                    // Always print if type is "reprint"
                    if (fullResponse.type === "reprint") {
                        printBarcode(response.PkgID);
                    }
                    // Auto-print for Relabel and TNT (only when print is on AND printer is online)
                    else if (autoPrintRef.current && printerOnlineRef.current) {
                        if (response["PO Name"] === "Relabel" || /^88\d{11}$/.test(val)) {
                            printBarcode(response.PkgID);
                        }
                    }

                    createLog(val, response.PO, response.Weight);
                    triggerResetDisplayCountdown();
                } else if (res.status === 401) {
                    logoutProcessor();
                    navigator("/login");
                } else if (res.status === 400) {
                    playError();
                    flashStatus("error");
                    toast.dismiss();
                    set_boxContents(null);
                    if (fullResponse?.message === "Unable to find scanned Item") {
                        set_scannedData({ PkgID: t("not_in_manifest"), PO: "ERROR", RemovalReason: "", "PO Name": "", Supplier: "" });
                        set_boxColour("ERROR");
                        toast.error(t("not_in_manifest"));
                    } else {
                        toast.error(t("invalid_scan"));
                        set_boxColour("#FFFFFF");
                    }
                    timer.pauseFor(ERROR_PAUSE_MS);
                    createLog(val, "ERROR", 0);
                    triggerResetDisplayCountdown();
                } else {
                    playError();
                    flashStatus("error");
                    toast.dismiss(); toast.error(t("server_error"));
                    timer.pauseFor(ERROR_PAUSE_MS);
                    createLog(val, "ERROR", 0);
                    resetDisplay();
                }
            });

            set_loading(false);
            set_scanValue("");
        }
    };

    const handleOversize = () => {
        if (!scannedData.PkgID) {
            toast.dismiss();
            toast.error(t("nothing_scanned"));
            return;
        }
        if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
        const newColour = scannedData.RemovalReason === "Overstock" ? "RED" : "YELLOW";
        set_scannedData(prev => ({ ...prev, PO: newColour }));
        set_boxColour(newColour);
        flashStatus("success");
        playSuccess();
        createLog(scannedData.PkgID, newColour, 0);
        triggerResetDisplayCountdown();
    };

    if (stage === "bf" && activeLocation) {
        return (
            <BfScanSheet
                location={activeLocation}
                resumeFromCache={bfResumeFromCache}
                onBack={() => navigator("/goods-in")}
                onLeave={() => {
                    clearLocationCache();
                    setActiveLocation(null);
                    setBfResumeFromCache(false);
                    setStage("location");
                    set_scanValue("");
                }}
            />
        );
    }

    // Prompt copy depends on stage: the location-scan pre-step takes over
    // the command line entirely so an operator who hasn't paired their
    // station yet can't mistake the empty item-scan UI for a working one.
    const commandText = stage === "location"
        ? t("scan_location_to_begin")
        : (scannedData.PkgID ? t(scannedData.PO.replace("-", "_").toLowerCase()) : t("scan_item"));
    const commandWords = commandText.toUpperCase().split(/[\s-]+/).filter(Boolean);
    const showSupplier = !!scannedData.PkgID && (/regainables/i.test(scannedData.Supplier) || /Amazon B-Stock/i.test(scannedData.Supplier));

    let inlineIcon: JSX.Element | null = null;
    if (scannedData.PkgID && scanStatus !== "error") {
        if (scanStatus === "success") {
            // Always show the tick during the success flash, regardless of colour.
            const tickClass = isDarkBg ? "s2-inline-success-light" : isLightGreen ? "s2-inline-success-dark" : "s2-inline-success";
            inlineIcon = (
                <div className={`s2-inline-status ${tickClass}`} key="tick">
                    <CheckCircle size={140} strokeWidth={3} />
                </div>
            );
        } else if (boxColour === "GREEN-HOT") {
            // After the tick fades (scanStatus idle), the GREEN-HOT emoji
            // takes the slot for the remainder of the display window.
            inlineIcon = (
                <div className="s2-inline-status" key="hot">
                    <img className="scan-inline-img" src={greenHotImg} alt="Green Hot" />
                </div>
            );
        } else if (boxColour === "GREEN-COLD") {
            inlineIcon = (
                <div className="s2-inline-status" key="cold">
                    <img className="scan-inline-img" src={greenColdImg} alt="Green Cold" />
                </div>
            );
        }
    }

    return (
        <div
            className={`scan-panel ${boxColour && boxColour !== "#FFFFFF" ? "scan-panel-coloured" : ""} ${isDarkBg ? "scan-light" : ""} ${isStorageDevice ? "scan-storage-device" : ""} ${isError ? "scan-error" : ""} ${showBoxInfo ? "scan-panel--boxinfo" : ""}`}
            style={{ backgroundColor: (colours[boxColour] || "white") }}
            onMouseDown={preventScanPageButtonFocus}
            onClick={blurClickedScanPageButton}
        >
            <ScanFlashOverlay active={scanStatus === "error"} />
            <div className="page-title-bar">
                <h1 className="page-title">{t("scan_sheet")}</h1>
                <ScoreDisplay total={score.total} />
                <button className="page-back-btn" onClick={() => navigator("/goods-in")}><ArrowLeft size={20} /></button>
            </div>

            <ProgressBar progress={timer.state.progress} visible={timer.state.active} />

            {showBoxInfo && (
                <div className="scan-boxinfo" data-testid="scan-boxinfo">
                    <div className="scan-boxinfo-head">
                        <div className="scan-boxinfo-head-side" />
                        <div className="scan-boxinfo-head-text">
                            <div className="scan-boxinfo-po">{commandText}</div>
                            <div className="scan-boxinfo-sku">{scannedData.PkgID}</div>
                        </div>
                        <div className="scan-boxinfo-head-side scan-boxinfo-head-side--right">
                            {showSupplier && <div className="scan-boxinfo-supplier">{scannedData.Supplier}</div>}
                            {scanStatus === "success" && (
                                <CheckCircle className="scan-boxinfo-tick" size={40} strokeWidth={3} />
                            )}
                        </div>
                    </div>
                    {boxInfoRows.length > 0 ? (
                        <>
                            <div className="scan-boxinfo-summary">
                                <span>{t("box_info_units", { count: boxInfoTotals.units })}</span>
                                <span className="scan-boxinfo-summary-sep">&middot;</span>
                                <span>{t("box_info_total_rrp", { value: fmtMoney(boxInfoTotals.rrp) })}</span>
                            </div>
                            <div className="scan-boxinfo-table">
                                <div className="scan-boxinfo-row scan-boxinfo-row--head">
                                    <span>{t("box_info_col_asin")}</span>
                                    <span className="scan-boxinfo-col-title-head">{t("box_info_col_title")}</span>
                                    <span className="scan-boxinfo-num">{t("box_info_col_units")}</span>
                                    <span className="scan-boxinfo-num">{t("box_info_col_rrp")}</span>
                                </div>
                                <div className="scan-boxinfo-body" ref={boxInfoBodyRef}>
                                    {boxInfoVisible.map((row, i) => (
                                        <div key={`${row.Asin}-${i}`} className="scan-boxinfo-row" data-boxinfo-row>
                                            <span className="scan-boxinfo-asin">{row.Asin}</span>
                                            <span className="scan-boxinfo-title" title={row.ItemDesc}>{capTitle(row.ItemDesc, boxInfoTitleMax)}</span>
                                            <span className="scan-boxinfo-num">{row.Units}</span>
                                            <span className="scan-boxinfo-num">{fmtMoney(row.UnitCost)}</span>
                                        </div>
                                    ))}
                                    {(boxInfoFit === null || boxInfoHidden > 0) && (
                                        <div className="scan-boxinfo-more" data-boxinfo-more>
                                            {t("box_info_more", { count: boxInfoHidden })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="scan-box-contents-none" data-testid="scan-box-contents-none">{t("box_info_none")}</div>
                    )}
                </div>
            )}

            <TimeBonusPopup
                seconds={timeBonus}
                color={timeBonusColor}
                streak={streakApi.streak}
                isBest={isBestTime}
                bumpKey={timeBonusKeyRef.current}
                darkBackground={isDarkBg}
            />

            {!showBoxInfo && (
            <div className="scan-centre">
                <div className="scan-command-wrap">
                    {!scannedData.PkgID && <ScanStatusIcons status={scanStatus === "error" ? "idle" : scanStatus} />}
                    <div className={`scan-command  ${scanStatus === "error" ? "scan-command-hidden" : ""}`}>
                        {commandWords.map((word, i) => (
                            <FitWord key={i}>{word}</FitWord>
                        ))}
                    </div>
                    {/* Hint listing the kinds of barcode that resolve at this
                        stage. Only shown pre-pairing — once the operator's
                        scanned a location and stage flips to "items" the
                        item-scan UI takes over and the hint becomes noise. */}
                    {stage === "location" && (
                        <div className={`scan-command-sub ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                            {t("scan_location_sub")}
                        </div>
                    )}
                    {showSupplier && (
                        <div className={`scan-supplier ${scanStatus === "error" ? "scan-command-hidden" : ""}`}>
                            {scannedData.Supplier}
                        </div>
                    )}
                </div>

                <div className={`scan-feedback-area ${scanStatus === "error" ? "scan-command-hidden" : ""}`}>
                    {scannedData.PkgID && (
                        <div className={`scan-feedback`}>
                            <p className="scan-feedback-id scan-pkgid">{scannedData.PkgID}</p>
                        </div>
                    )}
                </div>

                {inlineIcon}
            </div>
            )}

            <HiddenScanInput
                value={scanValue}
                onChange={set_scanValue}
                onSubmit={doSubmit}
                disabled={loading}
                printConnected={printChecking ? undefined : (autoPrint && printerOnline)}
                printFlash={printFlash}
                onPrintClick={openPrinterModal}
                canReprint={!!lastPrintValue}
                onReprintClick={() => { if (lastPrintValue) printBarcode(lastPrintValue); }}
                onKeyboardToggle={(open) => {
                    const was = keyboardOpenRef.current;
                    keyboardOpenRef.current = open;
                    if (timer.state.active && was !== open) {
                        timer.extend(open ? KEYBOARD_BONUS_MS : -KEYBOARD_BONUS_MS);
                    }
                }}
                onSuspectScan={() => { suspectScanRef.current = true; }}
                enableTntMode
                extraToggle={stage === "items" ? (
                    <button
                        type="button"
                        className="scan-oversize-btn"
                        onClick={(e) => { handleOversize(); (e.currentTarget as HTMLButtonElement).blur(); }}
                        aria-label={t("oversize")}
                        title={t("oversize")}
                    >
                        <Maximize2 size={44} strokeWidth={2.4} />
                    </button>
                ) : undefined}
                centreContent={lastScannedValue ? (
                    <span className="scan-toggle-centre-content">{t("last_scanned", { value: lastScannedValue })}</span>
                ) : undefined}
            />


            {/* Printer selection modal */}
            {showPrinterSelect && (
                <PrinterSelectModal
                    autoPrint={autoPrint}
                    printMode={printMode}
                    onSelect={(printer) => {
                        setShowPrinterSelect(false);
                        setAutoPrint(true);
                        setPrintMode("network");
                        setPrinterOnline(true);
                        localStorage.setItem("scansheet-auto-print", "true");
                    }}
                    onConnectLocal={() => {
                        setShowPrinterSelect(false);
                        setAutoPrint(true);
                        setPrintMode("local");
                        setPrinterOnline(true);
                        localStorage.setItem("scansheet-auto-print", "true");
                    }}
                    onDisconnect={() => {
                        setShowPrinterSelect(false);
                        setAutoPrint(false);
                        setPrintMode(null);
                        setPrinterOnline(false);
                        localStorage.setItem("scansheet-auto-print", "false");
                    }}
                    onClose={() => setShowPrinterSelect(false)}
                />
            )}
        </div>
    );
};

export default ScanSheet;
